import { type ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks';
import { useGraphQLQuery } from './useGraphQLRequest';

const ProfessionalPoolContextGql = graphql(`
  query GetProfessionalPoolContext($professional_id: Int!, $pool_id: Int!) {
    professional(id: $professional_id) {
      id
      pool_onboarding_status: pool_id_onboarding_status(pool_id: $pool_id)
      pool_pqs: professional_qualifications_for_pool(pool_id: $pool_id) {
        id
      }
    }
  }
`);

type ProfessionalPoolContextResult = ResultOf<typeof ProfessionalPoolContextGql>;
export type ProfessionalPoolPq = NonNullable<
  NonNullable<ProfessionalPoolContextResult['professional']>['pool_pqs']
>[number];

export type UseProfessionalPoolContextResult = {
  poolOnboardingStatus: string | null | undefined;
  poolPqs: ProfessionalPoolPq[];
  hasPoolPq: boolean;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
};

export const useProfessionalPoolContext = (
  poolId: number | null | undefined,
): UseProfessionalPoolContextResult => {
  const { currentUser } = useCurrentUser();
  const professionalId = currentUser?.professional?.id;
  const enabled = !!professionalId && poolId !== null && poolId !== undefined;

  const { data, isLoading, isInitialLoading, refetch } =
    useGraphQLQuery<ProfessionalPoolContextResult>({
      enabled,
      operationName: 'GetProfessionalPoolContext',
      query: ProfessionalPoolContextGql,
      variables: {
        professional_id: professionalId,
        pool_id: poolId ?? 0,
      },
    });

  const professional = data?.professional;
  const poolPqs = professional?.pool_pqs ?? [];

  return {
    poolOnboardingStatus: professional?.pool_onboarding_status,
    poolPqs,
    hasPoolPq: poolPqs.length > 0,
    isLoading: enabled ? isLoading : isInitialLoading,
    refetch,
  };
};
